import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Modal from './Modal';
import { formatCurrency } from '../CoreFunctions';
import { toast } from 'react-toastify';

const ValuationsModal = ({ isOpen, onClose, assetId, onValuationsUpdated }) => {
  const [valuations, setValuations] = useState({ book: [], market: [] });
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ type: '', date: '', value: '', value_source: '' });

  const fetchValuations = async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/balance_sheet/assets/valuations/${assetId}/`);
      setValuations(res.data || { book: [], market: [] });
    } catch (e) {
      console.error('Failed to load valuations:', e);
      toast.error('Failed to load valuations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchValuations();
    }
  }, [isOpen, assetId]);

  const handleEdit = (v) => {
    setEditingId(v.id);
    setEditForm({
      type: v.type,
      date: v.date,
      value: v.value,
      value_source: v.value_source
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSave = async (id) => {
    const endpoint = editForm.type === 'Book Value' ? 'book-valuations' : 'market-valuations';
    try {
      await axios.patch(`/api/balance_sheet/assets/${endpoint}/${id}/`, {
        date: editForm.date,
        value: editForm.value,
        value_source: editForm.value_source
      });
      toast.success('Valuation updated');
      setEditingId(null);
      fetchValuations();
      if (onValuationsUpdated) onValuationsUpdated();
    } catch (e) {
      console.error('Failed to update valuation:', e);
      toast.error('Failed to update valuation');
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this valuation?')) return;
    const endpoint = type === 'Book Value' ? 'book-valuations' : 'market-valuations';
    try {
      await axios.delete(`/api/balance_sheet/assets/${endpoint}/${id}/`);
      toast.success('Valuation deleted');
      fetchValuations();
      if (onValuationsUpdated) onValuationsUpdated();
    } catch (e) {
      console.error('Failed to delete valuation:', e);
      toast.error('Failed to delete valuation');
    }
  };

  const allValuations = [...valuations.book, ...valuations.market].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Valuations" width={800}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
      ) : (
        <div className="valuations-list-container">
          <table className="table" style={{ width: '100%', borderCollapse: 'collapse', color: '#e5e7eb' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #374151', textAlign: 'left' }}>
                <th style={{ padding: '8px' }}>Type</th>
                <th style={{ padding: '8px' }}>Date</th>
                <th style={{ padding: '8px' }}>Value</th>
                <th style={{ padding: '8px' }}>Source</th>
                <th style={{ padding: '8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {allValuations.map((v) => (
                <tr key={`${v.type}-${v.id}`} style={{ borderBottom: '1px solid #1f2937' }}>
                  {editingId === v.id && editForm.type === v.type ? (
                    <>
                      <td style={{ padding: '8px' }}>{v.type}</td>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="date"
                          className="form-control"
                          value={editForm.date}
                          onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                          style={{ backgroundColor: '#1f2937', color: '#fff', border: '1px solid #374151' }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          value={editForm.value}
                          onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                          style={{ backgroundColor: '#1f2937', color: '#fff', border: '1px solid #374151' }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="text"
                          className="form-control"
                          value={editForm.value_source}
                          onChange={(e) => setEditForm({ ...editForm, value_source: e.target.value })}
                          style={{ backgroundColor: '#1f2937', color: '#fff', border: '1px solid #374151' }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <button className="btn btn-sm btn-success" onClick={() => handleSave(v.id)} style={{ marginRight: '5px' }}>Save</button>
                        <button className="btn btn-sm btn-secondary" onClick={handleCancelEdit}>Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '8px' }}>{v.type}</td>
                      <td style={{ padding: '8px' }}>{v.date}</td>
                      <td style={{ padding: '8px' }}>{formatCurrency(v.value)}</td>
                      <td style={{ padding: '8px' }}>{v.value_source}</td>
                      <td style={{ padding: '8px' }}>
                        <button className="btn btn-sm btn-primary" onClick={() => handleEdit(v)} style={{ marginRight: '5px' }}>Edit</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(v.id, v.type)}>Delete</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {allValuations.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>No valuations found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};

export default ValuationsModal;
